import { graphql } from "babel-plugin-relay/macro";
import React, { Suspense, useEffect } from "react";
import { useSelector } from "react-redux";
import { useLazyLoadQuery, useQueryLoader } from "react-relay";
import { useMatch } from "react-router-dom";
import styled from "styled-components";
import { ContentNodeScreen_Query } from "@generated/ContentNodeScreen_Query.graphql";
import { NodeLeftFrame_Query } from "@generated/NodeLeftFrame_Query.graphql";
import { UseNodeLogic_LimitedAuthorizationsFragment$key } from "@generated/UseNodeLogic_LimitedAuthorizationsFragment.graphql";
import { ContentNodesButtons } from "./ContentNodesButtons";
import { ModuleRatingSummaryComponent } from "./parts/module-rating-summary";
import { RootNodeDescription } from "./RootNodeDescription";
import { RootNodeTitle } from "./RootNodeTitle";
import { WissenwirktAttachmentHeftDisplay } from "./WissenwirktAttachmentHeftDisplay";
import { DateTimeDisplay } from "../../../infecto-lms-webapp/components/DateTimeDisplay.component";
import { selectProgressBar } from "../../../infecto-lms-webapp/redux/slices/progress-bar.slice";
import { LMS_WEBAPP_CONFIG_IMPL } from "../../../infecto-lms-webapp-impl/config";
import {
	getLearnOpportunityPath,
	getRootAndContentNodePath,
	LEARN_OPPORTUNITY_ID_URL_PARAM,
	ROOT_AND_CONTENT_NODE_PARAMS,
} from "../../../infecto-lms-webapp-impl/router/routes/auth/nodes.routes";
import { NodeCardFooterContainer } from "../../containers/NodeCardFooterContainer";
import { NodeCardLearnProgressContainer } from "../../containers/NodeCardLearnProgress.container";
import { useNodeLogic } from "../../utils/UseNodeLogic";
import { GoBackButtonComponent } from "../navigation/GoBackButton.component";
import { NodeCardBadgeComponent } from "../nodecard/NodeCardBadgeComponent";
import { NodeCardDateComponent } from "../nodecard/NodeCardDate.component";
import { NodeCardDurationAndAwardComponent } from "../nodecard/NodeCardDurationAndAward.component";
import { NodeCardProgressBarComponent } from "../nodecard/NodeCardProgressBar.component";
import { NodeCardProgressBarTextComponent } from "../nodecard/NodeCardProgressBarText.component";
import { NodeIconComponent } from "../nodecard/NodeIcon.component";

const QUERY = graphql`
	query NodeLeftFrame_Query($learnOpportunityId: ID!) {
		node(id: $learnOpportunityId) {
			id
			... on LearnOpportunityV2 {
				id
				...UseNodeLogic_NodeFragment
				...NodeIcon_NodeFragment
				...NodeCardBadgeComponent_Fragment
				...NodeCardDurationAndAwardComponent_Fragment
			}
		}
		Viewer {
			Auth {
				currentUser {
					user {
						...DefaultContainer_UserDataFragment
						...UserStatusMessage_UserFragment
					}
				}
			}
		}
	}
`;

interface OwnProps {
	rootId: string;
	nodeWithoutUserLearnStateFragmentRef?: UseNodeLogic_LimitedAuthorizationsFragment$key;
	ignoreUserLearnState: boolean;
	isModuleExpiredAndFailed?: boolean;
	isContentNodeScreen: boolean;
}

export const NodeLeftFrame = ({
	rootId,
	nodeWithoutUserLearnStateFragmentRef,
	ignoreUserLearnState,
	isModuleExpiredAndFailed,
	isContentNodeScreen,
}: OwnProps) => {
	const query = useLazyLoadQuery<NodeLeftFrame_Query>(
		QUERY,
		{
			learnOpportunityId: rootId!,
		},
		{ fetchPolicy: "network-only" },
	);
	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	const [_, loadQuery] = useQueryLoader<ContentNodeScreen_Query>(QUERY);

	const contentSubmissionProgressPercentage = useSelector(selectProgressBar);

	useEffect(() => {
		loadQuery({ learnOpportunityId: rootId! as string }, { fetchPolicy: "network-only" });
	}, [contentSubmissionProgressPercentage, rootId, loadQuery]);

	const {
		learnOpportunity,
		isShowingBadge,
		hasNoLekButActiveContentSubmission,
		progressBarState,
		componentColor,
		numIssuedCertificates,
		doesNodeExpiresBeforeUserGetsUnblocked,
		hasCanNotBeRestarteAfterPassedConfig,
	} = useNodeLogic({
		learnOpportunityFragmentRef: query.node!,
		nodeWithoutUserLearnStateFragmentRef,
		ignoreUserLearnState,
	});
	let contentNodeId;
	contentNodeId = useMatch(getRootAndContentNodePath(ROOT_AND_CONTENT_NODE_PARAMS))?.params
		.contentId;

	if (!contentNodeId) {
		contentNodeId = useMatch(getLearnOpportunityPath(LEARN_OPPORTUNITY_ID_URL_PARAM))?.params
			.learnOpportunityId;
	}

	const isShowingProgressBar =
		learnOpportunity.structureDefinition.extension
			?.firstLearnOpportunityIdWithMultipleChoiceElement &&
		learnOpportunity.structureDefinition.extension
			?.firstLearnOpportunityIdWithMultipleChoiceElement == contentNodeId;
	const onlyAdminVisibilityDateTime =
		learnOpportunity?.structureDefinition?.extension?.dateTimeOnlyAdminsVisibility;
	const internalCode = learnOpportunity.structureDefinition.extension?.internalCode;
	return (
		<Wrapper className="col-12 md:bg-transparent tk-bg-white lg:h-full flex flex-column align-content-start overflow-y-auto">
			<GoBackButtonComponent />
			{onlyAdminVisibilityDateTime !== null && (
				<div className="text-primary mt-3">
					Sichtbar ab:{" "}
					<DateTimeDisplay hideTimezone value={onlyAdminVisibilityDateTime as string} />
				</div>
			)}
			<CardContainer className="px-4 py-4 mt-4">
				<div className="flex w-full">
					{isShowingBadge ? (
						<NodeCardBadgeComponent
							ignoreUserLearnState={ignoreUserLearnState}
							learnOpportunityFragmentRef={learnOpportunity}
							color={componentColor}
							isNodeFinished={
								numIssuedCertificates > 0 ||
								hasNoLekButActiveContentSubmission ||
								hasCanNotBeRestarteAfterPassedConfig
							}
						/>
					) : null}
					<div className="ml-auto cursor-pointer">
						<NodeIconComponent learnOpportunityFragmentRef={learnOpportunity} />
					</div>
				</div>
				<div className="flex-column w-full h-full mt-4 align-items-center justify-content-center">
					<RootNodeTitle title={learnOpportunity.structureDefinition.title} />
					<div className="">
						{learnOpportunity.shortDescription ? (
							<RootNodeDescription description={learnOpportunity.shortDescription} />
						) : null}
					</div>
					<InfoContainer className="flex flex-column w-full justify-content-center align-items-center">
						{learnOpportunity.structureDefinition.extension?.configConsequences?.isRewardedTo ? (
							<NodeCardDateComponent
								doesNodeExpiresBeforeUserGetsUnblocked={doesNodeExpiresBeforeUserGetsUnblocked}
								isUserRewardedWithCertificate={numIssuedCertificates > 0}
								learnOpportunityFragmentRef={learnOpportunity}
								hasCanNotBeRestarteAfterPassed={hasCanNotBeRestarteAfterPassedConfig}
								hasNoLekButActiveContentSubmission={hasNoLekButActiveContentSubmission}
							></NodeCardDateComponent>
						) : null}
						<NodeCardDurationAndAwardComponent
							isUserRewardedWithCertificate={numIssuedCertificates > 0}
							learnOpportunityFragmentRef={learnOpportunity}
						/>
						<div className="flex w-full justify-content-center align-items-center">
							<ModuleRatingSummaryComponent isAlwaysVisible={true} nodeRef={learnOpportunity.id} />
						</div>
					</InfoContainer>
				</div>

				<ContentNodesButtons learnOpportunityFragmentRef={learnOpportunity} />
				<Suspense>
					{isContentNodeScreen ? (
						isShowingProgressBar && isModuleExpiredAndFailed ? (
							<div className="mb-1">
								<div className="flex-column w-full">
									<NodeCardLearnProgressContainer>
										<NodeCardProgressBarTextComponent isBold={false}>
											Lernfortschritt
										</NodeCardProgressBarTextComponent>
										<NodeCardProgressBarTextComponent isBold={true}>
											100%
										</NodeCardProgressBarTextComponent>
									</NodeCardLearnProgressContainer>

									<NodeCardProgressBarComponent
										progressBarState={progressBarState}
										componentColor={componentColor}
										isBlocked={false}
										percentage={100}
									/>
								</div>
							</div>
						) : (
							<div className="mb-1">
								<NodeCardFooterContainer
									progressBarState={progressBarState}
									componentColor={LMS_WEBAPP_CONFIG_IMPL.brandColor}
									learnOpportunityFragmentRef={learnOpportunity}
								></NodeCardFooterContainer>
							</div>
						)
					) : null}
				</Suspense>
				<WissenwirktAttachmentHeftDisplay
					learnOpportunityFragmentRef={learnOpportunity}
					marginTopValue={"22px"}
				/>
				{internalCode && (
					<div className="flex row w-full justify-content-end">
						<ModuleNumberWrapper>Modulnummer: {internalCode}</ModuleNumberWrapper>
					</div>
				)}
			</CardContainer>
		</Wrapper>
	);
};
const InfoContainer = styled.div`
	display: flex;
	justify-content: center;
	align-items: center;
	gap: 8px;
`;
const ModuleNumberWrapper = styled.div`
	display: flex;
	align-self: end;
	color: #787878;
	font-size: 12px;
	font-style: normal;
	font-weight: 400;
	line-height: normal;
	margin-top: 5px;
`;
const Wrapper = styled.div`
	@media screen and (min-width: 1200px) {
		max-width: 28%;
	}
`;

const CardContainer = styled.div`
	border-radius: 8px;
	border: 1px solid #ebf5ea;
	background: #fff;
	background-blend-mode: soft-light;
	backdrop-filter: blur(35px);
`;
